import {gql} from "@apollo/client";

const STATUS_PAGE_FIELDS = gql`
  fragment StatusPageFields on PublicStatusPage {
    id
    name
    slug
    description
    description_html
    logo_url
    favicon_url
    company_website_url
    contact_email
    timezone
    used_timezone
    time_format
    default_status_date_range
    default_history_date_range
    max_visible_days
    max_visible_component_days
    show_status_tab
    show_history_tab
    show_active_incidents
    show_component_response_time
    show_summary_metrics
    show_past_incidents
    show_component_history
    show_history_snake
    hide_empty_tabs_status
    hide_empty_tabs_history
    allow_drill_down
    allow_pdf_report
    allow_search_indexing
    layout_preset
    show_component_bars
    show_component_descriptions
    show_overall_status
    show_branding_footer
    allow_subscriptions
    allow_subscriptions_email
    allow_subscriptions_rss
    allow_subscriptions_slack
    allow_subscriptions_sms
    allow_subscriptions_webhook
    slack_oauth_url
    custom_header_bg_color_hex
    custom_header_text_color_hex
    custom_header_html_inspire
    custom_footer_html_inspire
    custom_css_inspire
    google_analytics_code
    has_v4_google_analytics_code
    theme
    uptime_calculation_type
    global_is_operational
    has_components_under_maintenance_state
    has_components_under_critical_state
    components {
      id
      name
      is_group
      status
      status_display
      description
      cached_response_time
      response_time_data
      performance_data_type
      has_drill_down
      service_id
      last_updated_at
      subcomponents {
        id
        name
        is_group
        status
        status_display
        description
        cached_response_time
        response_time_data
        performance_data_type
        has_drill_down
        service_id
        last_updated_at
      }
    }
    metrics {
      id
      name
    }
    active_incidents {
      id
      name
      starts_at
      ends_at
      duration
      incident_type
      incident_state
      include_in_global_metrics
      latest_update_incident_state
      latest_update_description
      updates {
        id
        updated_at
        description_html
        incident_state
        incident_state_display
      }
      affected_components {
        id
        name
        status
        status_display
      }
    }
    upcoming_maintenance {
      id
      name
      starts_at
      ends_at
      duration
      incident_type
      incident_state
      include_in_global_metrics
      latest_update_incident_state
      latest_update_description
      updates {
        id
        updated_at
        description_html
        incident_state
        incident_state_display
      }
      affected_components {
        id
        name
        status
        status_display
      }
    }
    component_status_metadata {
      value
      rank
      display
      color
      is_operational
    }
    visibility_level
    uptime_calculation_type
  }
`;

export const GQL_PUBLIC_STATUSPAGE_QUERY = gql`
  query PublicStatusPage($slug: String!) {
    publicStatusPage(slug: $slug) {
      ...StatusPageFields
    }
  }
  ${STATUS_PAGE_FIELDS}
`;

export const GQL_RESOLVE_STATUSPAGE_BY_HOST = gql`
  query ResolveStatusPageByHost($host: String!) {
    resolveStatusPageByHost(host: $host) {
      slug
      serving_mode
    }
  }
`;

export const GQL_PRIVATE_STATUSPAGE_QUERY = gql`
  query PrivateStatusPage($id: Int!) {
    privateStatusPage(id: $id) {
      ...StatusPageFields
    }
  }
  ${STATUS_PAGE_FIELDS}
`;

export const GQL_PUBLIC_STATUSPAGE_HISTORY_QUERY = gql`
  query PublicStatusPageHistory($slug: String!, $startDate: String!, $endDate: String!, $sort: String) {
    publicStatusPageHistory(slug: $slug, startDate: $startDate, endDate: $endDate, sort: $sort) {
      past_incidents {
        id
        name
        starts_at
        ends_at
        duration
        incident_type
        incident_state
        include_in_global_metrics
        latest_update_incident_state
        latest_update_description
        updates {
          id
          updated_at
          description_html
          incident_state
          incident_state_display
        }
        affected_components {
          id
          name
          status
          status_display
        }
      }
      component_history {
        id
        name
        is_group
        major_outage
        partial_outage
        degraded_performance
        avg_response_time
        uptime_pct
        outages
        downtime_secs
        performance_data_type
        service_url
        service_uptime_sla
        service_id
        children {
          id
          name
          is_group
          major_outage
          partial_outage
          degraded_performance
          avg_response_time
          uptime_pct
          outages
          downtime_secs
          performance_data_type
          service_url
          service_uptime_sla
          service_id
        }
      }
      uptime_calculation_type
      downtime_history {
        service_id
        start_ts
        end_ts
      }
      global_metrics {
        uptime_pct
        outages
        downtime_secs
      }
    }
  }
`;

export const GQL_PUBLIC_STATUSPAGE_SUBSCRIBE = gql`
  mutation PublicStatusPageSubscribe($data: PublicStatusPageSubscribeInput!) {
    publicStatusPageSubscribe(data: $data) {
      ... on PublicStatusPageSubscribeSuccess {
        status
        data {
          verification_required
          rss_url
        }
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_PRIVATE_STATUSPAGE_HISTORY_QUERY = gql`
  query PrivateStatusPageHistory($id: Int!, $startDate: String!, $endDate: String!, $sort: String) {
    privateStatusPageHistory(id: $id, startDate: $startDate, endDate: $endDate, sort: $sort) {
      past_incidents {
        id
        name
        starts_at
        ends_at
        duration
        incident_type
        incident_state
        include_in_global_metrics
        latest_update_incident_state
        latest_update_description
        updates {
          id
          updated_at
          description_html
          incident_state
          incident_state_display
        }
        affected_components {
          id
          name
          status
          status_display
        }
      }
      component_history {
        id
        name
        is_group
        major_outage
        partial_outage
        degraded_performance
        avg_response_time
        uptime_pct
        outages
        downtime_secs
        performance_data_type
        service_url
        service_uptime_sla
        service_id
        children {
          id
          name
          is_group
          major_outage
          partial_outage
          degraded_performance
          avg_response_time
          uptime_pct
          outages
          downtime_secs
          performance_data_type
          service_url
          service_uptime_sla
          service_id
        }
      }
      uptime_calculation_type
      downtime_history {
        service_id
        start_ts
        end_ts
      }
      global_metrics {
        uptime_pct
        outages
        downtime_secs
      }
    }
  }
`;

export const GQL_PUBLIC_METRIC_DATAPOINTS_QUERY = gql`
  query PublicMetricDatapoints($slug: String!, $metricId: Int!, $timerange: String) {
    publicMetricDatapoints(slug: $slug, metricId: $metricId, timerange: $timerange) {
      datapoints {
        ts
        value
      }
    }
  }
`;

export const GQL_PRIVATE_METRIC_DATAPOINTS_QUERY = gql`
  query PrivateMetricDatapoints($id: Int!, $metricId: Int!, $timerange: String) {
    privateMetricDatapoints(id: $id, metricId: $metricId, timerange: $timerange) {
      datapoints {
        ts
        value
      }
    }
  }
`;

const COMPONENT_DRILLDOWN_FIELDS = `
  component_id
  component_name
  check_type
  check_type_display
  address
  current_status
  current_status_display
  is_up
  uptime_pct
  check_interval
  downtime_secs
  outages
  last_downtime_at
  alerts_count
  last_alert_at
  uptime_calculation_type
  downtime_history {
    service_id
    start_ts
    end_ts
  }
  response_time_data {
    location
    data
  }
  response_time_high
  response_time_low
  response_time_avg
`;

export const GQL_PUBLIC_COMPONENT_DRILLDOWN_QUERY = gql`
  query PublicComponentDrilldown($slug: String!, $serviceId: Int!, $startDate: String, $endDate: String) {
    publicComponentDrilldown(slug: $slug, serviceId: $serviceId, startDate: $startDate, endDate: $endDate) {
      ${COMPONENT_DRILLDOWN_FIELDS}
    }
  }
`;

export const GQL_PRIVATE_COMPONENT_DRILLDOWN_QUERY = gql`
  query PrivateComponentDrilldown($id: Int!, $serviceId: Int!, $startDate: String, $endDate: String) {
    privateComponentDrilldown(id: $id, serviceId: $serviceId, startDate: $startDate, endDate: $endDate) {
      ${COMPONENT_DRILLDOWN_FIELDS}
    }
  }
`;

export const GQL_PUBLIC_STATUSPAGE_PDF_QUERY = gql`
  query PublicStatusPagePdf($slug: String!, $startDate: String, $endDate: String) {
    publicStatusPagePdf(slug: $slug, startDate: $startDate, endDate: $endDate) {
      data
      filename
    }
  }
`;

export const GQL_PRIVATE_STATUSPAGE_PDF_QUERY = gql`
  query PrivateStatusPagePdf($id: Int!, $startDate: String, $endDate: String) {
    privateStatusPagePdf(id: $id, startDate: $startDate, endDate: $endDate) {
      data
      filename
    }
  }
`;
